import React, { FC, useCallback, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { font } from '@styles/fonts';
import { fullWidth, scale } from '@components/frame';
import { VersionHeaderProps, VersionPickerAnchorPosition } from './types';
import i18n from 'i18n-js';
import moment from 'moment';
import { SCREEN_PADDING_VERTICAL_FOR_ITEM } from '@v2/pages/common/constants/base.constants';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import { isWeb } from '@helpers';

const VersionHeader: FC<VersionHeaderProps> = ({ C, version, onPress, isExpanded, disabled, onToggleFavorite }) => {
  const containerRef = useRef<View>(null);

  const formatEvent = (value?: string | null): string => {
    if (!value) {
      return '--';
    }

    const parsed = moment(value);
    if (!parsed.isValid()) {
      return value;
    }

    return parsed.format('DD MMM YYYY HH:mm');
  };

  const versionLabel = useMemo(() => `${i18n.t('Version')} ${version.version}`, [version.version]);
  const firstLabel = i18n.t('First');
  const lastLabel = i18n.t('Last');
  const unknownLabel = i18n.t('Unknown');
  const inactiveLabel = i18n.t('Inactive');

  const caretIcon = isExpanded ? 'chevronUp' : 'chevronBottomLight';

  const renderHeaderContent = (hovered?: boolean) => {
    const showFavoriteIcon = version.isFavorite || !isWeb || hovered;
    const favoriteIconColor = version.isFavorite ? C('Label/Light/Primary') : C('Label/Light/Tertiary');

    return (
      <View style={[styles.content, disabled && styles.disabled]}>
        <View style={styles.versionHeaderRow}>
          <View style={styles.versionTitle}>
            {onPress && !disabled && <Svg icon={caretIcon} width={scale(14)} height={scale(14)} color="Link/Light/Primary" />}
            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{versionLabel}</Text>
            {version.isActive === false && (
              <View style={[styles.inactiveBadge, { borderColor: C('Label/Light/Tertiary') }]}>
                <Text style={[font.Caption2, { color: C('Label/Light/Tertiary') }]}>{inactiveLabel}</Text>
              </View>
            )}
            {showFavoriteIcon ? (
              <TouchableOpacity
                disabled={version.isFavorite || !onToggleFavorite}
                onPress={onToggleFavorite}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Svg
                  icon={version.isFavorite ? 'starSelected' : 'starUnselected'}
                  width={scale(18)}
                  height={scale(18)}
                  color={favoriteIconColor}
                />
              </TouchableOpacity>
            ) : (
              <View style={{ width: scale(18), height: scale(18) }} />
            )}
          </View>

          {typeof version.pcsCount === 'number' && (
            <View style={styles.pcsBlock}>
              <Text style={[font['Time 2'], { color: C('Label/Light/Tertiary') }]}>{version.pcsCount}</Text>
              <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Pcs')}</Text>
            </View>
          )}
        </View>

        <View style={styles.metadataRow}>
          <View style={styles.metadataColumn}>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{firstLabel}</Text>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{version.firstMachine || unknownLabel}</Text>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{formatEvent(version.firstEvent)}</Text>
          </View>
          <View style={[styles.metadataColumn, styles.metadataColumnRight]}>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{lastLabel}</Text>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{version.lastMachine || unknownLabel}</Text>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{formatEvent(version.lastEvent)}</Text>
          </View>
        </View>
      </View>
    );
  };

  const handlePress = useCallback(() => {
    if (!onPress || disabled) {
      return;
    }

    const node = containerRef.current;
    if (node && typeof node.measure === 'function') {
      node.measure((_x, _y, width, height, pageX, pageY) => {
        const anchor: VersionPickerAnchorPosition = {
          top: pageY + height,
          bottom: pageY,
          left: pageX,
          right: Math.max(fullWidth - pageX - width, 0),
          width,
          height,
        };

        onPress(anchor);
      });
      return;
    }

    onPress({
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      width: 0,
      height: 0,
    });
  }, [disabled, onPress]);

  if (onPress && !disabled) {
    return (
      <TouchableElement onPress={handlePress}>
        {({ pressHoverColor, boxStyle, hovered }) => (
          <View ref={containerRef} style={[boxStyle, styles.container, { backgroundColor: C(pressHoverColor('Background/Light/Field')) }]}>
            {renderHeaderContent(hovered)}
          </View>
        )}
      </TouchableElement>
    );
  }

  return <View style={[styles.container, { backgroundColor: C('Background/Light/Field') }]}>{renderHeaderContent(false)}</View>;
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
    paddingHorizontal: scale(16),
  },
  // content: {
  //   gap: scale(12),
  // },
  disabled: {
    opacity: 0.6,
  },
  versionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  versionTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  pcsBlock: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: scale(4),
  },
  inactiveBadge: {
    paddingHorizontal: scale(8),
    paddingVertical: scale(2),
    borderRadius: scale(999),
    borderWidth: StyleSheet.hairlineWidth,
  },
  metadataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: scale(4),
  },
  metadataColumn: {
    flex: 1,
  },
  metadataColumnRight: {
    alignItems: 'flex-end',
  },
});

export default VersionHeader;
